"use client";

import { startTransition, useActionState, useEffect, useMemo, useRef, useState } from "react";

import type { Route } from "next";
import { useRouter, useSearchParams } from "next/navigation";

import { LuArrowLeft } from "react-icons/lu";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { getApiAxiosInstance } from "@/api/client-axios-instance";
import { resendOtpAction } from "@/app/(auth)/actions";
import { cn } from "@/app/_libs/utils/cn";
import { env } from "@/env";
import { useAuthStore } from "@/store/auth-store";

type OtpVerificationActionState = {
  success: boolean;
  errors?: string[];
  data?: { mobileNumber: string; otp: string };
  message?: string;
};

type OtpResendActionState = {
  success: boolean;
  errors?: string[];
  data?: { mobileNumber: string };
  message?: string;
};

interface OtpVerificationProps {
  length: number;
  onStepChange: (step: string) => void;
  steps: string[];
  resendTimer: number; // seconds
  otpId?: string;
}
const apiAxios = getApiAxiosInstance();
export default function OTPVerification({
  length,
  onStepChange,
  steps,
  resendTimer,
  otpId,
}: OtpVerificationProps) {
  const [verifyState, setVerifyState] = useState<OtpVerificationActionState>({ success: false });
  const [verifyPending, setVerifyPending] = useState(false);
  const [resendState, resendAction, resendPending] = useActionState<OtpResendActionState, FormData>(
    resendOtpAction,
    {
      success: false,
    },
  );
  const [otpValues, setOtpValues] = useState<string[]>(() => Array.from({ length }, () => ""));
  const [timer, setTimer] = useState<number>(resendTimer);
  const router = useRouter();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const searchParams = useSearchParams();
  const mobileNumber = searchParams?.get("mobileNumber") ?? null;
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const refs = useMemo(() => {
    inputRefs.current = Array.from({ length }, (_, idx) => inputRefs.current[idx] ?? null);
    return inputRefs.current;
  }, [length]);

  useEffect(() => {
    refs[0]?.focus();
  }, [refs]);

  useEffect(() => {
    if (timer <= 0) {
      return;
    }
    const interval = setInterval(() => setTimer((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (!resendState.success) return;
    setOtpValues(Array.from({ length }, () => ""));
    setTimer(resendTimer);
    refs[0]?.focus();
  }, [length, resendState, resendTimer, refs]);

  const handleBack = () => {
    onStepChange(steps[0] ?? "mobile-number-entry");
    const nextRoute = `/login/?step=${steps[0]}&mobileNumber=${mobileNumber}` as Route;
    router.replace(nextRoute);
  };

  const handleChange = (value: string, idx: number) => {
    const nextChar = value.slice(-1);
    if (!nextChar.match(/^\d?$/)) return;

    setOtpValues((prev) => {
      const next = [...prev];
      next[idx] = nextChar;
      return next;
    });

    if (nextChar && idx < length - 1) {
      refs[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (event.key === "Backspace") {
      setOtpValues((prev) => {
        const next = [...prev];
        if (next[idx]) {
          next[idx] = "";
          return next;
        }
        if (idx > 0) {
          refs[idx - 1]?.focus();
          next[idx - 1] = "";
        }
        return next;
      });
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isComplete || verifyPending) return;

    const otp = otpValues.join("");

    setVerifyPending(true);
    setVerifyState((prev) => ({ ...prev, errors: undefined }));

    try {
      const response = await apiAxios.post(`${env.NEXT_PUBLIC_APP_URL}/api/verify-otp`, {
        otp_id: otpId,
        otp_code: otp,
      });
      const responseData = response.data;
      setAccessToken(
        (responseData as { data?: { access_token?: string } })?.data?.access_token ?? null,
      );
      setVerifyState((prev) => ({
        ...prev,
        success: true,
        data: responseData,
      }));

      const redirectParam = new URLSearchParams(window.location.search).get("redirectTo");

      const redirectPath =
        redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
          ? redirectParam
          : "/client-management";

      // 👇 this guarantees same host + port
      window.location.href = `${window.location.origin}${redirectPath}`;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        err?.response?.data?.message ??
        (error instanceof Error ? error.message : null) ??
        "OTP verification failed";

      setVerifyState((prev) => ({
        ...prev,
        success: false,
        errors: [errorMessage],
      }));
    } finally {
      setVerifyPending(false);
    }
  };

  const handleResend = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (resendPending) return;
    const formData = new FormData(event.currentTarget);
    formData.set("otpId", otpId || "");
    startTransition(() => {
      resendAction(formData);
    });
  };

  const formattedTimer = `${String(Math.floor(timer / 60)).padStart(1, "0")}:${String(timer % 60).padStart(2, "0")} s`;
  const isComplete = otpValues.every((v) => v !== "") && otpValues.join("").length === length;
  const buttonLabel = verifyPending ? "Verifying..." : "Continue";
  const isDisabled = !isComplete || verifyPending;
  const verifyError = verifyState.errors?.[0];
  const resendError = resendState.errors?.[0];
  const canResend = timer <= 0;

  return (
    <section className="flex max-w-[448px] flex-col">
      <div className="flex justify-start">
        <Button
          type="button"
          onClick={handleBack}
          aria-label="Go back"
          variant="outline"
          size="sm"
          className="bg-secondary-secondary400 h-[44px] w-[44px] rounded-full border-none p-[10px]">
          <LuArrowLeft className="size-6" color="black" />
        </Button>
      </div>

      <div className="my-[40px] space-y-2">
        <h2 className="text-heading-2xl font-lora text-text-label--black">OTP Verification</h2>
        <p className="text-normal-sm font-geist text-text-label--gray">
          Enter the 6-digit code we sent to {mobileNumber}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="w-full">
        <input type="hidden" name="mobileNumber" value={mobileNumber ?? ""} />
        <input type="hidden" name="otp" value={otpValues.join("")} />

        <div className="mb-[24px] flex items-center gap-3">
          {otpValues.map((value, idx) => (
            <Input
              key={idx}
              ref={(el) => {
                refs[idx] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={value}
              onChange={(e) => handleChange(e.target.value, idx)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={cn(
                "text-normal-md text-typography-typography900 h-[52px] rounded-[4px] text-center",
                "focus:border-text-highlight focus-visible:border-text-highlight focus:ring-text-highlight/40 focus-visible:ring-text-highlight/40 border-[#9D9691] bg-transparent",
                value &&
                  "border-text-highlight focus:border-text-highlight focus-visible:border-text-highlight focus:ring-text-highlight/40 focus-visible:ring-text-highlight/40 bg-[#FFF7F5]",
              )}
            />
          ))}
        </div>
        {verifyError ? <p className="text-destructive mb-3 text-sm">{verifyError}</p> : null}
        {resendError ? <p className="text-destructive text-sm">{resendError}</p> : null}

        <Button
          type="submit"
          disabled={isDisabled}
          className="mb-[16px] h-10 w-full cursor-pointer rounded-[100px]">
          {buttonLabel}
        </Button>
      </form>

      {!canResend ? (
        <p className="text-typography-typography700 text-sm">
          Didn&apos;t get the code?{" "}
          <span className="font-geist text-text-highlight leading-normal font-bold">
            Resend Code in {formattedTimer}
          </span>
        </p>
      ) : (
        <form onSubmit={handleResend} className="flex items-center gap-2">
          <input type="hidden" name="mobileNumber" value={mobileNumber ?? ""} />
          <p className="text-typography-typography700 text-sm">Didn&apos;t get the code?</p>
          <Button
            type="submit"
            variant="link"
            disabled={resendPending}
            className="text-text-highlight h-auto cursor-pointer p-0 font-bold">
            {resendPending ? "Resending..." : "Resend"}
          </Button>
        </form>
      )}
    </section>
  );
}
